/**
 * server-py market data APIs.
 */

import { API_CONFIG } from '@/config/api';
import { apiFetch } from '@/lib/apiFetch';

const SPREAD_BASE = API_CONFIG.SPREAD_PATH_PREFIX;
const CANDLE_BASE = API_CONFIG.CANDLE_PATH_PREFIX;

export interface CandleDataPoint {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  avg_mid_price: number;
  tick_count: number;
}

export interface CandleResponse {
  data: CandleDataPoint[];
  statistics: {
    total_points: number;
  };
}

export interface CandleParams {
  exchange: string;
  inst_id: string;
  start_time: string;
  end_time: string;
  interval: number;
}

export interface SpreadDataPoint {
  timestamp: string;
  price1: number;
  price2: number;
  spread: number;
  spread_rate: number;
  sma_spread?: number | null;
  sma_spread_rate?: number | null;
}

export interface SpreadStatistics {
  mean_spread: number;
  std_spread: number;
  mean_spread_rate: number;
  std_spread_rate: number;
  sma_period: number;
  total_points: number;
}

export interface SpreadResponse {
  data: SpreadDataPoint[];
  statistics: SpreadStatistics;
}

export interface DetailedSpreadDataPoint {
  timestamp: string;
  bid_price1: number;
  ask_price1: number;
  bid_price2: number;
  ask_price2: number;
  long1_short2_close_spread: number;
  long1_short2_open_spread: number;
  short1_long2_open_spread: number;
  short1_long2_close_spread: number;
  mean_long1_short2_close_spread?: number | null;
  mean_long1_short2_open_spread?: number | null;
  mean_short1_long2_open_spread?: number | null;
  mean_short1_long2_close_spread?: number | null;
  median_long1_short2_close_spread?: number | null;
  median_short1_long2_close_spread?: number | null;
  long1_short2_open_line?: number | null;
  long1_short2_close_line?: number | null;
  short1_long2_open_line?: number | null;
  short1_long2_close_line?: number | null;
  mid_price1?: number;
  mid_price2?: number;
  long1_short2_mid_spread?: number;
  short1_long2_mid_spread?: number;
  median_long1_short2_mid_spread?: number | null;
  median_short1_long2_mid_spread?: number | null;
}

export interface Short1Long2MidSpreadDeviationStats {
  total_points: number;
  exceed_0_002_ratio: number;
  exceed_0_005_ratio: number;
  exceed_0_01_ratio: number;
  max_abs_deviation: number;
}

export interface DetailedSpreadResponse {
  data: DetailedSpreadDataPoint[];
  total_points?: number;
  short1_long2_mid_spread_deviation?: Short1Long2MidSpreadDeviationStats | null;
}

export interface IndexAdjustedSpreadDataPoint {
  timestamp: string;
  orderbook_price1: number;
  orderbook_price2: number;
  index_price1: number;
  index_price2: number;
  orderbook_spread: number;
  index_spread: number;
  adjusted_spread: number;
  sma_adjusted_spread?: number | null;
}

export interface IndexAdjustedSpreadStatistics {
  total_points: number;
  mean_adjusted_spread: number;
  std_adjusted_spread: number;
  sma_period: number;
}

export interface IndexAdjustedSpreadResponse {
  data: IndexAdjustedSpreadDataPoint[];
  statistics: IndexAdjustedSpreadStatistics;
  meta?: {
    interval: string;
    symbol1: string;
    symbol2: string;
    spread_unit?: string;
  };
}

export interface IndexAdjustedSpreadParams {
  exchange1: string;
  inst_id1: string;
  exchange2: string;
  inst_id2: string;
  start_time: string;
  end_time: string;
  interval: 60 | 300;
  symbol1?: string;
  symbol2?: string;
  sma_period?: number;
}

export interface SpreadParams {
  spread_type?: string;
  exchange1: string;
  inst_id1: string;
  exchange2: string;
  inst_id2: string;
  start_time: string; // ISO
  end_time: string;   // ISO
  interval: number;
  sma_period?: number | null;
  exchange1_fee?: number;
  exchange2_fee?: number;
  min_profit_percentage?: number;
}

export async function getCandles(params: CandleParams): Promise<CandleResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange);
  q.set('inst_id', params.inst_id);
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  q.set('interval', String(params.interval));

  const res = await apiFetch(`${CANDLE_BASE}/candles?${q.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<CandleResponse>;
}

export async function getSpread(params: SpreadParams): Promise<SpreadResponse> {
  const q = new URLSearchParams();
  q.set('spread_type', params.spread_type ?? 'mid');
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  q.set('interval', String(params.interval));
  if (params.sma_period != null) q.set('sma_period', String(params.sma_period));
  if (params.exchange1_fee != null) q.set('exchange1_fee', String(params.exchange1_fee));
  if (params.exchange2_fee != null) q.set('exchange2_fee', String(params.exchange2_fee));
  if (params.min_profit_percentage != null) q.set('min_profit_percentage', String(params.min_profit_percentage));

  const res = await apiFetch(`${SPREAD_BASE}/spread?${q.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<SpreadResponse>;
}

export async function getDetailedSpread(params: Omit<SpreadParams, 'spread_type'>): Promise<DetailedSpreadResponse> {
  const q = new URLSearchParams();
  q.set('spread_type', 'detailed');
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  q.set('interval', String(params.interval));
  if (params.sma_period != null) q.set('sma_period', String(params.sma_period));
  if (params.exchange1_fee != null) q.set('exchange1_fee', String(params.exchange1_fee));
  if (params.exchange2_fee != null) q.set('exchange2_fee', String(params.exchange2_fee));
  if (params.min_profit_percentage != null) q.set('min_profit_percentage', String(params.min_profit_percentage));

  const res = await apiFetch(`${SPREAD_BASE}/spread?${q.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<DetailedSpreadResponse>;
}

export async function getIndexAdjustedSpread(params: IndexAdjustedSpreadParams): Promise<IndexAdjustedSpreadResponse> {
  const q = new URLSearchParams();
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  q.set('interval', String(params.interval));
  if (params.symbol1) q.set('symbol1', params.symbol1);
  if (params.symbol2) q.set('symbol2', params.symbol2);
  if (params.sma_period != null) q.set('sma_period', String(params.sma_period));

  const res = await apiFetch(`${SPREAD_BASE}/spread/index-adjusted?${q.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<IndexAdjustedSpreadResponse>;
}

export async function getExchanges(): Promise<{ exchanges: string[] }> {
  const res = await apiFetch(`${SPREAD_BASE}/exchanges`, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ exchanges: string[] }>;
}

export interface InstrumentItem {
  inst_id: string;
  symbol?: string;
  base_asset?: string;
  quote_asset?: string;
}

export async function getInstruments(exchange: string): Promise<{ instruments: InstrumentItem[] }> {
  const q = new URLSearchParams({ exchange });
  const res = await apiFetch(`${SPREAD_BASE}/instruments?${q.toString()}`, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ instruments: InstrumentItem[] }>;
}
